import { useState, useEffect } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

const FREQ_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function ReportsPage() {
  const [reports, setReports] = useState<any[]>([]);
  const [reportTypes, setReportTypes] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState<number | null>(null);

  // New report form
  const [formType, setFormType] = useState('management_summary');
  const [formFreq, setFormFreq] = useState('weekly');
  const [formDayOfWeek, setFormDayOfWeek] = useState(0);
  const [formDayOfMonth, setFormDayOfMonth] = useState(1);
  const [formEmails, setFormEmails] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.getScheduledReports(),
        api.getReportTypes(),
      ]);
      setReports(r);
      setReportTypes(t);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleCreate() {
    try {
      const emails = formEmails.split(',').map(e => e.trim()).filter(Boolean);
      if (emails.length === 0) {
        alert('Please enter at least one email address');
        return;
      }
      await api.createScheduledReport({
        report_type: formType,
        frequency: formFreq,
        day_of_week: formFreq === 'weekly' ? formDayOfWeek : undefined,
        day_of_month: formFreq === 'monthly' ? formDayOfMonth : undefined,
        recipient_emails: emails,
      });
      setShowModal(false);
      setFormEmails('');
      await loadData();
    } catch (err: any) {
      alert(err.message);
    }
  }

  async function handleRunNow(reportId: number) {
    setSending(reportId);
    try {
      const result = await api.runReport(reportId);
      alert(`Report sent to ${result.recipients} recipient(s)`);
    } catch (err: any) {
      alert(err.message);
    }
    setSending(null);
  }

  async function handleToggle(reportId: number, isActive: boolean) {
    await api.updateScheduledReport(reportId, { is_active: !isActive });
    await loadData();
  }

  async function handleDelete(reportId: number) {
    if (!confirm('Delete this scheduled report?')) return;
    await api.deleteScheduledReport(reportId);
    await loadData();
  }

  return (
    <PortalLayout currentPage="reports">
      <div className="p-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900">Scheduled Reports</h1>
            <p className="text-sm text-gray-500 mt-1">Automated compliance reports sent via email</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800"
          >
            + New Report
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-3">No scheduled reports yet</p>
            <p className="text-sm text-gray-400">Set up automated compliance reports to keep your team informed</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r: any) => (
              <div key={r.id} className={`bg-white border rounded p-4 ${r.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900">{r.report_name}</p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>{r.is_active ? 'Active' : 'Paused'}</span>
                    </div>
                    <p className="text-sm text-gray-500">{r.report_description}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {FREQ_LABELS[r.frequency] || r.frequency}
                      {r.frequency === 'weekly' && r.day_of_week != null && ` on ${DAY_NAMES[r.day_of_week]}`}
                      {r.frequency === 'monthly' && r.day_of_month && ` on day ${r.day_of_month}`}
                      {' '}&bull; To: {r.recipient_emails?.join(', ')}
                      {r.last_sent && <> &bull; Last sent: {new Date(r.last_sent).toLocaleDateString('en-GB')}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRunNow(r.id)}
                      disabled={sending === r.id}
                      className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50"
                    >{sending === r.id ? 'Sending...' : 'Run Now'}</button>
                    <button
                      onClick={() => handleToggle(r.id, r.is_active)}
                      className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                    >{r.is_active ? 'Pause' : 'Resume'}</button>
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New report modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">New Scheduled Report</h3>

              <div className="mb-3">
                <label className="block text-sm text-gray-600 mb-1">Report Type</label>
                <select value={formType} onChange={e => setFormType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  {Object.entries(reportTypes).map(([key, val]: any) => (
                    <option key={key} value={key}>{val.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-sm text-gray-600 mb-1">Frequency</label>
                <select value={formFreq} onChange={e => setFormFreq(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {formFreq === 'weekly' && (
                <div className="mb-3">
                  <label className="block text-sm text-gray-600 mb-1">Day of Week</label>
                  <select value={formDayOfWeek} onChange={e => setFormDayOfWeek(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}

              {formFreq === 'monthly' && (
                <div className="mb-3">
                  <label className="block text-sm text-gray-600 mb-1">Day of Month</label>
                  <input type="number" min={1} max={28} value={formDayOfMonth}
                    onChange={e => setFormDayOfMonth(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">Recipient Emails (comma separated)</label>
                <input type="text" value={formEmails} onChange={e => setFormEmails(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="tom@company.co.uk, jane@company.co.uk" />
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={handleCreate} className="px-4 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800">Create Report</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
